import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Lock, Minus, Plus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { formatINR, tierRate } from "@/lib/format";
import ProductImg from "@/components/ProductImg";

const BOX_UNITS = ["box", "boxes", "pack", "packs", "set", "sets", "bundle"];

// Box/pack SKUs: rate & stock are per pack. Piece SKUs: rate & stock per pc, sold in steps of pack_size.
export function packInfo(product, v) {
  const unit = String(v?.unit || product.unit || "pcs").toLowerCase().replace(/\.$/, "");
  const m = /(?:pack|set|box)\s*of\s*(\d+)/i.exec(v?.name || product.name || "");
  return { isBox: BOX_UNITS.includes(unit), perPack: m ? Number(m[1]) : 0, step: v?.pack_size || 1, moq: v?.moq || 1 };
}

export function SizeChips({ variants, selectedId, onSelect, testPrefix, max = 8 }) {
  const shown = variants.slice(0, max);
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={`${testPrefix}-sizes`}>
      {shown.map((v) => {
        const active = v.id === selectedId;
        const label = v.size || v.variant_label;
        return (
          <button
            key={v.id}
            type="button"
            disabled={!v.in_stock}
            title={v.in_stock ? v.variant_label : `${v.variant_label} · Sold out`}
            onClick={(e) => { e.stopPropagation(); onSelect(v.id); }}
            data-testid={`${testPrefix}-size-${label}`}
            className={`min-w-[2.25rem] max-w-[7rem] truncate px-2 py-1 rounded-lg border text-[11px] font-semibold transition-colors ${
              !v.in_stock
                ? "border-dashed border-slate-300 text-slate-400 line-through cursor-not-allowed bg-slate-100"
                : active
                ? "bg-slate-900 border-slate-900 text-white"
                : "bg-white border-slate-200 text-slate-700 hover:border-slate-500"
            }`}
          >
            {label}
          </button>
        );
      })}
      {variants.length > max && (
        <span className="px-1.5 py-1 text-[11px] text-slate-400">+{variants.length - max}</span>
      )}
    </div>
  );
}

function QtyStepper({ qty, onDec, onInc, testPrefix }) {
  return (
    <div className="flex items-center rounded-full border border-slate-200 shrink-0">
      <button className="p-1.5 sm:p-2 text-slate-500 hover:text-slate-900" onClick={onDec} data-testid={`${testPrefix}-qty-dec`} aria-label="Decrease">
        <Minus className="h-3.5 w-3.5" />
      </button>
      <span className="w-7 sm:w-9 text-center text-sm font-semibold font-mono-num" data-testid={`${testPrefix}-qty`}>{qty}</span>
      <button className="p-1.5 sm:p-2 text-slate-500 hover:text-slate-900" onClick={onInc} data-testid={`${testPrefix}-qty-inc`} aria-label="Increase">
        <Plus className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

export function cartLine(product, v) {
  const many = (product.variants || []).length > 1;
  return {
    ...product,
    id: v.id,
    name: many ? `${product.name} — ${v.variant_label}` : product.name,
    rate: v.rate,
    moq: v.moq,
    pack_size: v.pack_size,
    unit: v.unit || product.unit,
    stock_on_hand: v.stock_on_hand,
    tier_prices: v.tier_prices || [],
    image_url: product.image_url,
    sku: v.sku || product.sku,
  };
}

export default function ProductCard({ product, eager = false, idPrefix = "" }) {
  const { user, approved } = useAuth();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const variants = useMemo(() => product.variants || [], [product.variants]);
  const firstInStock = variants.find((v) => v.in_stock) || variants[0];
  const [selId, setSelId] = useState(firstInStock?.id);
  const current = variants.find((v) => v.id === selId) || firstInStock || {};
  const [qty, setQty] = useState(current.moq || 1);
  const pack = current.pack_size || 1;
  const tid = `${idPrefix}${product.id}`;
  const open = () => navigate(`/product/${current.id || product.id}`);

  const select = (id) => {
    const v = variants.find((x) => x.id === id);
    setSelId(id);
    setQty(v?.moq || 1);
  };

  const unitRate = approved && current.rate != null ? tierRate({ rate: current.rate, tier_prices: current.tier_prices || [] }, qty) : null;

  const pk = packInfo(product, current);
  const unitWord = pk.isBox ? "pack" : "pc";
  const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
  const sizeLabel = pk.isBox && pk.perPack > 1
    ? `Select Size (Pack of ${pk.perPack}${(product.colours || []).length ? "" : " Assorted Colours"})`
    : "Select Size";

  const handleAdd = () => {
    if (!current.in_stock) return;
    if (qty < (current.moq || 1)) return toast.error(`Minimum order is ${current.moq}.`);
    if (qty > (current.stock_on_hand || 0)) return toast.error(`Only ${current.stock_on_hand} in stock for this size.`);
    addItem(cartLine(product, current), qty);
    toast.success(`${pk.isBox ? "1 pack · " : ""}${product.name} (${current.size || current.variant_label}) added to cart`);
  };

  return (
    <div
      data-testid={`product-card-${tid}`}
      className="group flex flex-col rounded-2xl border border-slate-200 bg-white overflow-hidden hover:shadow-lg hover:shadow-slate-200/70 hover:border-slate-300 transition-[box-shadow,border-color] duration-300"
    >
      <div className="relative aspect-square overflow-hidden bg-white cursor-pointer" onClick={open}>
        <ProductImg
          src={product.image_url}
          fallback={product.image_large}
          alt={product.alt || product.name}
          brand={product.brand || ""}
          eager={eager}
          testId={`product-img-${tid}`}
          className="h-full w-full object-contain p-2 group-hover:scale-[1.04] transition-transform duration-500"
        />
        <div className="absolute top-2 left-2 flex flex-col gap-1 items-start">
          {product.is_new && (
            <span className="rounded-full bg-[#FFDA00] text-slate-900 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider" data-testid={`new-badge-${tid}`}>New</span>
          )}
        </div>
        {approved && (
          <span className={`absolute top-2 right-2 rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${current.in_stock ? "bg-emerald-600" : "bg-rose-600"}`} data-testid={`stock-badge-${tid}`}>
            {current.in_stock ? `${plural(current.stock_on_hand, unitWord)} left` : "Sold out"}
          </span>
        )}
      </div>

      <div className="flex flex-col flex-1 p-3 sm:p-4 gap-2">
        <div className="flex items-center justify-between gap-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
          <span className="truncate">{product.brand || product.category}</span>
          {product.design_code && <span className="font-mono-num shrink-0">{product.design_code}</span>}
        </div>
        <h3 className="font-heading font-semibold text-sm sm:text-[15px] text-slate-900 leading-snug line-clamp-2 cursor-pointer hover:text-slate-600" onClick={open} data-testid={`product-name-${tid}`}>
          {product.name}
        </h3>
        {variants.length > 1 && (
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 mb-1.5" data-testid={`size-label-${tid}`}>{sizeLabel}</div>
            <SizeChips variants={variants} selectedId={current.id} onSelect={select} testPrefix={`card-${tid}`} />
          </div>
        )}

        <div className="mt-auto pt-2">
          {!approved ? (
            <Button size="sm" variant="outline" className="w-full rounded-full text-xs" data-testid={`unlock-price-${tid}`}
              onClick={() => navigate(user ? "/account" : "/register")}>
              <Lock className="h-3.5 w-3.5 mr-1" /> {user ? "Awaiting approval" : "Login for price"}
            </Button>
          ) : (
            <>
              <div className="flex items-end justify-between gap-2 mb-2">
                <div className="min-w-0">
                  <div className="font-mono-num text-lg sm:text-xl font-bold text-slate-900 leading-none" data-testid={`price-${tid}`}>
                    {formatINR(unitRate)}<span className="text-xs font-sans font-medium text-slate-500"> / {unitWord}</span>
                  </div>
                  {pk.isBox && pk.perPack > 1 && unitRate != null && (
                    <div className="text-[11px] text-slate-500 mt-1 font-mono-num" data-testid={`unit-price-${tid}`}>({formatINR(Math.round((unitRate / pk.perPack) * 100) / 100)} / pc)</div>
                  )}
                  {!pk.isBox && pk.step > 1 && unitRate != null && (
                    <div className="text-[11px] text-slate-500 mt-1" data-testid={`pack-note-${tid}`}>Sold in packs of {pk.step} · {formatINR(unitRate * pk.step)} / pack</div>
                  )}
                  <div className="text-[11px] text-slate-400 mt-1 truncate" data-testid={`moq-${tid}`}>
                    MOQ: {pk.isBox ? `${pk.moq} Pack${pk.moq === 1 ? "" : "s"}` : `${pk.moq} pcs`}
                  </div>
                </div>
                <QtyStepper qty={qty} testPrefix={tid}
                  onDec={() => setQty((q) => Math.max(current.moq || 1, q - pack))}
                  onInc={() => setQty((q) => q + pack)} />
              </div>
              <Button size="sm" className="w-full rounded-full bg-slate-900 hover:bg-black text-white" onClick={handleAdd}
                disabled={!current.in_stock} data-testid={`add-to-cart-${tid}`}>
                <ShoppingCart className="h-4 w-4 mr-1.5" /> {current.in_stock ? "Add to cart" : "Sold out"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
